import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useCookies } from "react-cookie";
import { useEffect, useState } from "react";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";

import SimpleBar from "simplebar-react";
import "simplebar/dist/simplebar.min.css";

import Swal from "sweetalert2";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

import usersEndpoints from "../../../services/userServices";
import pluginrequests from "../../../services/pluginrequests";
import removeMediaFromUrl from "../../../hooks/loadImage";
import helpers from "../../../hooks/HelperFunctions";
import { BRAND, DIVIDER, ICON_TILE_SX, MUTED, PRIMARY_BTN_SX, TEXT } from "./styles";

const config = require("../../../config/settings.json");
const { escapeHtml } = helpers;

function mapUnlockedContact(contact) {
  return {
    image: contact.profile_pic_url_hd || null,
    fullname: contact.full_name || contact.username,
    handle: contact.username,
    email: contact.email || "",
    phone: helpers.getContactPhone(contact),
    manager_name: helpers.getActiveManager(contact).manager_name || "",
  };
}

function EmptyState({ icon, title, text }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        py: 4,
        borderRadius: 2,
        border: `1px dashed ${DIVIDER}`,
        bgcolor: "#FAFAFA",
      }}
    >
      <Box sx={{ ...ICON_TILE_SX, width: 44, height: 44, borderRadius: "12px", mx: "auto", mb: 1.5 }}>
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: 14, color: TEXT }}>{title}</Typography>
      <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 12.5 }}>{text}</Typography>
    </Box>
  );
}

function TabLabel({ title, count, active }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <span>{title}</span>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          fontWeight: 800,
          color: active ? "#fff" : BRAND,
          bgcolor: active ? BRAND : "rgba(255,82,0,0.08)",
          transition: "background-color 0.18s ease, color 0.18s ease",
        }}
      />
    </Stack>
  );
}

// Underline sub-tabs, so they read as nested under the page's segmented tabs.
const SUB_TABS_SX = {
  minHeight: 0,
  mb: 2,
  borderBottom: `1px solid ${DIVIDER}`,
  "& .MuiTabs-indicator": { bgcolor: BRAND, height: 2.5, borderRadius: "2px 2px 0 0" },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13.5,
    minHeight: 42,
    px: 0.5,
    mr: 3,
    minWidth: 0,
    color: MUTED,
    "&:hover": { color: TEXT },
  },
  "& .MuiTab-root.Mui-selected": { color: TEXT },
};

function InfluencerRow({ influencer, showContact, onShowDetails, busy }) {
  return (
    <ListItem
      sx={{
        px: 1.5,
        pr: onShowDetails ? 17 : 1.5,
        borderRadius: "10px",
        alignItems: "flex-start",
        "&:hover": { bgcolor: "rgba(255,82,0,0.04)" },
      }}
      secondaryAction={
        onShowDetails && (
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            onClick={() => onShowDetails(influencer)}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ ...PRIMARY_BTN_SX, fontSize: 12.5, px: 1.75 }}
          >
            Show details
          </Button>
        )
      }
    >
      <ListItemAvatar>
        <Avatar src={influencer.image ? removeMediaFromUrl(influencer.image) : undefined} />
      </ListItemAvatar>
      <ListItemText
        primary={influencer.fullname}
        primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: TEXT }}
        secondaryTypographyProps={{ component: "div" }}
        secondary={
          <Box>
            <Typography
              component={"a"}
              href={`${config.INSTAGRAM_BASE}${influencer.handle}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: BRAND, fontWeight: 600, fontSize: 13, textDecoration: "none" }}
            >
              @{influencer.handle}
            </Typography>
            {showContact && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.5, sm: 2 }}
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                {influencer.email && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <EmailOutlinedIcon sx={{ fontSize: 15, color: MUTED }} />
                    <Typography
                      component="a"
                      href={`mailto:${influencer.email.split(",")[0].trim()}`}
                      sx={{ fontSize: 13, color: "inherit", textDecoration: "none", wordBreak: "break-all" }}
                    >
                      {influencer.email}
                    </Typography>
                  </Stack>
                )}
                {influencer.phone && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <PhoneOutlinedIcon sx={{ fontSize: 15, color: MUTED }} />
                    <Typography sx={{ fontSize: 13 }}>
                      {influencer.phone}
                      {influencer.manager_name ? ` (Manager: ${influencer.manager_name})` : ""}
                    </Typography>
                  </Stack>
                )}
                {!influencer.email && !influencer.phone && (
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    No contact details on file
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        }
      />
    </ListItem>
  );
}

function InfluencerList({ items, showContact, keyPrefix, onShowDetails, busyHandle }) {
  return (
    <SimpleBar autoHide={true} style={{ maxHeight: "45vh" }}>
      <List disablePadding>
        {items.map((influencer, index) => (
          <Box component={"div"} key={`${keyPrefix}_${influencer.handle}_${index}`}>
            <InfluencerRow
              influencer={influencer}
              showContact={showContact}
              onShowDetails={onShowDetails}
              busy={busyHandle === influencer.handle}
            />
            {index < items.length - 1 && <Divider sx={{ borderColor: DIVIDER }} />}
          </Box>
        ))}
      </List>
    </SimpleBar>
  );
}

function RequestedContactsList({ onCreditsChange }) {
  const [cookies] = useCookies(["user"]);
  const [unlockedInfluencers, setUnlockedInfluencers] = useState([]);
  const [readyInfluencers, setReadyInfluencers] = useState([]);
  const [requestedInfluencers, setRequestedInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState("");
  const [busyHandle, setBusyHandle] = useState(null);

  // Unlock a requested influencer whose details have arrived: same endpoint (and
  // credit charge) as the influencer table's "Show Details" button.
  const handleShowDetails = async (item) => {
    const confirm = await Swal.fire({
      title: `Unlock @${escapeHtml(item.handle)}?`,
      text: "This will use 1 credit to show the contact details.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Show details",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
    });
    if (!confirm.isConfirmed) return;

    setBusyHandle(item.handle);
    let response;
    try {
      response = await pluginrequests.getContactNumber({ uniqueId: item.id, handle: item.handle });
    } catch (err) {
      response = { success: false, message: err.message };
    }
    setBusyHandle(null);

    if (!response?.success) {
      Swal.fire({ icon: "error", title: "Couldn't unlock", text: response?.message || "Please try again." });
      return;
    }

    const d = response.data || {};
    const unlocked = {
      ...item,
      email: d.email || "",
      phone: helpers.formatPhoneList(d.phone),
      manager_name: d.name_of_manager || "",
    };
    setReadyInfluencers((prev) => prev.filter((i) => i.handle !== item.handle));
    setUnlockedInfluencers((prev) => [unlocked, ...prev.filter((i) => i.handle !== item.handle)]);
    if (d.balance !== undefined) onCreditsChange?.(d.balance);

    const row = (label, value) =>
      value ? `<p style="margin:6px 0"><b>${label}:</b> ${escapeHtml(value)}</p>` : "";
    Swal.fire({
      icon: "success",
      title: escapeHtml(item.fullname),
      html:
        row("Instagram", `@${item.handle}`) +
        row("Phone", unlocked.phone) +
        row("Email", unlocked.email) +
        row("Manager", unlocked.manager_name) +
        `<p style="margin-top:12px;color:${MUTED};font-size:13px">Saved to your Unlocked contacts.</p>`,
      confirmButtonColor: BRAND,
    });
  };

  useEffect(() => {
    if (!cookies.user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const getdataurl = "_id=" + cookies.user._id;
    Promise.allSettled([
      usersEndpoints.viewedInfluencersList(getdataurl),
      usersEndpoints.requestedInfluencersList(getdataurl),
    ]).then(([viewed, requested]) => {
      if (cancelled) return;
      if (viewed.status === "fulfilled" && viewed.value?.success === true && viewed.value.data) {
        setUnlockedInfluencers(viewed.value.data.map(mapUnlockedContact));
      }
      if (requested.status === "fulfilled" && requested.value?.success === true) {
        setRequestedInfluencers(requested.value.data || []);
        const ready = requested.value.ready || [];
        setReadyInfluencers(ready);
        if (ready.length > 0) setActiveTab(1); // open straight to newly available details
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: BRAND }} />
      </Box>
    );
  }

  const sections = [
    {
      key: "unlocked",
      title: "Unlocked",
      subtitle: "Influencers whose contact details you unlocked with credits",
      items: unlockedInfluencers,
      showContact: true,
      emptyIcon: <LockOpenOutlinedIcon />,
      emptyTitle: "No unlocked contacts yet",
      emptyText: "Contacts you unlock from the influencer search will appear here.",
    },
    {
      key: "ready",
      title: "Details Ready",
      subtitle: "You requested these and their details have arrived. Unlock to view.",
      items: readyInfluencers,
      showContact: false,
      onShowDetails: handleShowDetails,
      emptyIcon: <MarkEmailReadOutlinedIcon />,
      emptyTitle: "Nothing new yet",
      emptyText: "When details arrive for an influencer you requested, they'll show up here.",
    },
    {
      key: "requested",
      title: "Requested",
      subtitle: "Influencers you've asked us to find contact details for. We're still looking.",
      items: requestedInfluencers,
      showContact: false,
      emptyIcon: <PersonSearchOutlinedIcon />,
      emptyTitle: "No requested contacts yet",
      emptyText: "Influencers whose contact details you request will appear here.",
    },
  ];
  const section = sections[activeTab];
  const needle = query.trim().toLowerCase();
  const visibleItems = needle
    ? section.items.filter((i) =>
        [i.fullname, i.handle, i.email, i.phone].some((v) => v && String(v).toLowerCase().includes(needle))
      )
    : section.items;

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={SUB_TABS_SX}
      >
        {sections.map((s, i) => (
          <Tab
            key={s.key}
            disableRipple
            label={<TabLabel title={s.title} count={s.items.length} active={activeTab === i} />}
          />
        ))}
      </Tabs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 12.5 }}>
          {section.subtitle}
        </Typography>
        <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={section.items.length === 0}
            placeholder={`Search ${section.title.toLowerCase()} contacts`}
            sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search">
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
      </Stack>

      {section.items.length === 0 ? (
        <EmptyState icon={section.emptyIcon} title={section.emptyTitle} text={section.emptyText} />
      ) : visibleItems.length === 0 ? (
        <EmptyState
          icon={<SearchIcon />}
          title="No matches"
          text={`No ${section.title.toLowerCase()} contacts match "${query.trim()}".`}
        />
      ) : (
        <InfluencerList
          items={visibleItems}
          showContact={section.showContact}
          keyPrefix={section.key}
          onShowDetails={section.onShowDetails}
          busyHandle={busyHandle}
        />
      )}
    </Box>
  );
}

export default RequestedContactsList;
